import { useMemo } from 'react';
import useSWR from 'swr';
import { useGlobalModules } from 'contexts';
import { parseDiscountsConditions, isProductEligibleForFreeItem, getFreeItemLabelText } from './useDiscountFreeItem';

export const useFreeItemLabel = (shopifyProductId, locale) => {
  const globalModules = useGlobalModules();
  const discountIDs = globalModules.settings?.freeItem?.discountIDs || [];

  const fetcher = (...urls) => {
    const f = u => fetch(u).then(r => r.json());
    if (urls.length > 1) return Promise.all(urls.map(f));
    return f(urls);
  };

  const urls = discountIDs.map(
    id => `/api/shopify/get-automatic-discount-by-id/?discountId=${id}&locale=${locale}`
  );

  const { data: discounts } = useSWR(
    discountIDs.length ? urls : null,
    fetcher
  );

  const { data: collectionsResponse } = useSWR(
    shopifyProductId
      ? `/api/shopify/utils/getProductCollections?productId=${shopifyProductId}&locale=${locale}`
      : null,
    url => fetch(url).then(r => r.json())
  );

  const isLoading = discountIDs.length > 0 && (!discounts || !collectionsResponse);

  return useMemo(() => {
    if (isLoading) return undefined;

    const conditions = parseDiscountsConditions(discounts);
    const qualifyingProductIds = [];
    const qualifyingCollectionIds = [];

    conditions?.forEach(condition => {
      if (condition?.products) qualifyingProductIds.push(...condition.products);
      if (condition?.collections) qualifyingCollectionIds.push(...condition.collections);
    });

    if (!qualifyingProductIds.length && !qualifyingCollectionIds.length) return null;

    const productId = `gid://shopify/Product/${shopifyProductId}`;
    const collectionIds = collectionsResponse?.status === 200 ? collectionsResponse.data : [];

    const isEligible = isProductEligibleForFreeItem(
      productId,
      collectionIds,
      qualifyingProductIds,
      qualifyingCollectionIds
    );

    return isEligible ? getFreeItemLabelText(globalModules.translations) : null;
  }, [discounts, collectionsResponse, shopifyProductId, globalModules.translations]);
};

export default useFreeItemLabel;
